import type { DiaClient } from './client';
import { getDb } from './dexie';
import {
  deadCount,
  drainOutbox,
  enqueue,
  nextRetryAt,
  pendingCount,
  recoverStaleOps,
  resetBackoff,
  type EnqueueInput,
} from './outbox';
import { drainPhotos, nextPhotoRetryAt, recoverStalePhotos, resetPhotoBackoff } from './photos';

/**
 * The sync engine: one per app, started at boot, stopped never.
 *
 * It ticks on app open, on regaining connectivity, on visibility change, every
 * 60 seconds while open (Section 11), and, when something is waiting out a
 * backoff, at the moment that backoff ends. A tick drains the outbox and the
 * photo queue independently, so a write that cannot land does not hold up a
 * photo and a photo that cannot land does not hold up a tick.
 *
 * The retry wake up is the one that is easy to leave out, and leaving it out is
 * defect R3: the ladder says 2, 4, 8, 16, 32 seconds and the 60 second interval
 * rounded every rung of it up to a minute, so a write the island will never
 * accept sat in front of the queue for about four minutes instead of about two.
 * The ladder only means anything if somebody is awake for it.
 *
 * Only one tick runs at a time. That is what makes "exactly once" true when the
 * phone comes back from airplane mode and fires online, visibilitychange and
 * the interval within the same second.
 */

export type SyncReason =
  | 'open'
  | 'online'
  | 'visible'
  | 'interval'
  | 'manual'
  | 'enqueue'
  | 'retry';

export type SyncStatus = {
  online: boolean;
  syncing: boolean;
  /** Writes still waiting. This is the number the badge shows. */
  pending: number;
  /**
   * Writes the island refused for good, parked out of the queue's way. Nothing
   * is lost: they are shown with a retry and a discard (defect D5).
   */
  dead: number;
  photosPending: number;
  lastSyncedAt: number | null;
  lastError: string | null;
  lastReason: SyncReason | null;
};

export type SyncEngineOptions = {
  intervalMs?: number;
  /** Test seam. Defaults to the real clock. */
  now?: () => number;
  /** Test seam. Defaults to navigator.onLine, and true where there is no navigator. */
  isOnline?: () => boolean;
};

/**
 * Which reason wins when two triggers land together. online and manual are the
 * only ones that clear a backoff, so they outrank the rest.
 */
const REASON_RANK: Record<SyncReason, number> = {
  online: 4,
  manual: 4,
  open: 3,
  visible: 2,
  enqueue: 1,
  interval: 0,
  // A backoff that has run out is exactly as strong as the interval: it is time
  // to try again, and it is not news that the network came back.
  retry: 0,
};

function strongerReason(a: SyncReason | null, b: SyncReason): SyncReason {
  if (!a) return b;
  return REASON_RANK[b] > REASON_RANK[a] ? b : a;
}

const IDLE: SyncStatus = {
  online: true,
  syncing: false,
  pending: 0,
  dead: 0,
  photosPending: 0,
  lastSyncedAt: null,
  lastError: null,
  lastReason: null,
};

export class SyncEngine {
  private readonly client: DiaClient;
  private readonly intervalMs: number;
  private readonly now: () => number;
  private readonly onlineProbe: () => boolean;

  private status: SyncStatus = { ...IDLE };
  private listeners = new Set<(status: SyncStatus) => void>();
  private timer: ReturnType<typeof setInterval> | null = null;
  private wake: ReturnType<typeof setTimeout> | null = null;
  private running: Promise<void> | null = null;
  private queuedReason: SyncReason | null = null;
  private detach: Array<() => void> = [];

  constructor(client: DiaClient, options: SyncEngineOptions = {}) {
    this.client = client;
    this.intervalMs = options.intervalMs ?? 60_000;
    this.now = options.now ?? (() => Date.now());
    this.onlineProbe =
      options.isOnline ?? (() => (typeof navigator === 'undefined' ? true : navigator.onLine));
    this.status.online = this.onlineProbe();
  }

  /** Attaches the four triggers and runs the first tick. Safe to call twice. */
  start(): void {
    if (this.timer) return;

    this.timer = setInterval(() => void this.tick('interval'), this.intervalMs);

    if (typeof window !== 'undefined') {
      const online = () => {
        this.patch({ online: true });
        void this.tick('online');
      };
      const offline = () => this.patch({ online: false });
      window.addEventListener('online', online);
      window.addEventListener('offline', offline);
      this.detach.push(() => window.removeEventListener('online', online));
      this.detach.push(() => window.removeEventListener('offline', offline));
    }

    if (typeof document !== 'undefined') {
      const visible = () => {
        if (document.visibilityState === 'visible') void this.tick('visible');
      };
      document.addEventListener('visibilitychange', visible);
      this.detach.push(() => document.removeEventListener('visibilitychange', visible));
    }

    // Anything the last run left mid flight goes back in its queue before the
    // first drain (defect D3). This is a fresh process: no upload is in the
    // air, whatever the rows say.
    void this.recover().then(
      () => this.tick('open'),
      () => this.tick('open'),
    );
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.clearWake();
    for (const off of this.detach) off();
    this.detach = [];
  }

  /** The shortest useful sleep, so a queue cannot spin the phone's battery. */
  private static readonly MIN_WAKE_MS = 250;

  private clearWake(): void {
    if (this.wake) clearTimeout(this.wake);
    this.wake = null;
  }

  /**
   * Sleeps until the first thing in either queue is due, and no longer.
   *
   * Only a started engine does this. A test that drives the clock by hand owns
   * the cadence itself, and a timer firing behind it would be noise.
   */
  private async scheduleWake(): Promise<void> {
    this.clearWake();
    if (!this.timer) return;

    const [ops, photos] = await Promise.all([nextRetryAt(), nextPhotoRetryAt()]);
    const due = [ops, photos].filter((at): at is number => at !== null);
    if (due.length === 0) return;

    const delay = Math.min(
      this.intervalMs,
      Math.max(SyncEngine.MIN_WAKE_MS, Math.min(...due) - this.now()),
    );

    this.wake = setTimeout(() => {
      this.wake = null;
      void this.tick('retry');
    }, delay);
    // Node only, and only so a test process is never held open by a queue.
    (this.wake as unknown as { unref?: () => void }).unref?.();
  }

  subscribe(listener: (status: SyncStatus) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getSnapshot(): SyncStatus {
    return this.status;
  }

  /**
   * Puts a killed app's half finished work back in its queues. Section 13 item
   * 2 in one call: reopen and the queue resumes, with nothing sent twice
   * (every write is matched on its client_id and every upload is an upsert on
   * a path fixed at capture time).
   */
  async recover(): Promise<{ ops: number; photos: number }> {
    const at = this.now();
    const [ops, photos] = await Promise.all([recoverStaleOps(at), recoverStalePhotos(at, 0)]);
    await this.refreshCounts();
    return { ops, photos };
  }

  /** Queues a write and nudges the engine. The screen does not wait for either. */
  async enqueue(input: EnqueueInput) {
    const op = await enqueue(input, this.now());
    await this.refreshCounts();
    void this.tick('enqueue');
    return op;
  }

  /**
   * One pass, and never two at the same time.
   *
   * A caller that arrives while a pass is running does not start a second one
   * and does not lose its reason either: the reason is queued, the running pass
   * picks it up when it finishes, and every caller waits for the whole thing.
   * That matters because the reasons are not equal. Coming back from airplane
   * mode clears the backoff; a 60 second interval does not.
   */
  async tick(reason: SyncReason = 'manual'): Promise<void> {
    this.queuedReason = strongerReason(this.queuedReason, reason);
    if (this.running) return this.running;

    this.running = (async () => {
      try {
        while (this.queuedReason) {
          const next = this.queuedReason;
          this.queuedReason = null;
          // A tick never rejects. A closed database, a browser that revoked
          // storage, a vendor SDK that threw: none of those should take the app
          // down, and all of them should show up in the badge instead.
          try {
            await this.runOnce(next);
          } catch (err) {
            this.patch({
              syncing: false,
              lastError: err instanceof Error ? err.message : String(err),
            });
          }
        }
      } finally {
        this.running = null;
      }
    })();

    return this.running;
  }

  private async runOnce(reason: SyncReason): Promise<void> {
    const online = this.onlineProbe();
    this.patch({ syncing: true, online, lastReason: reason });

    if (!online) {
      // Nothing to try. The badge still shows how much is waiting, and the
      // phone's own online event is what wakes us, so no timer is set.
      await this.refreshCounts();
      this.clearWake();
      this.patch({ syncing: false });
      return;
    }

    const at = this.now();

    // The phone just told us the network is back, the app was opened, or a
    // person pulled to refresh. Either way the queue should not sit out a
    // backoff it earned in a dead spot half an hour ago.
    if (reason === 'online' || reason === 'manual' || reason === 'open') {
      await Promise.allSettled([resetBackoff(at), resetPhotoBackoff(at)]);
    }

    const [ops, photos] = await Promise.allSettled([
      drainOutbox(this.client, at),
      drainPhotos(this.client, at),
    ]);

    const errors: string[] = [];
    if (ops.status === 'rejected') errors.push(String(ops.reason));
    if (photos.status === 'rejected') errors.push(String(photos.reason));

    await this.refreshCounts();
    // Whatever is left is on a backoff. Be awake when it ends (defect R3).
    await this.scheduleWake();
    this.patch({
      syncing: false,
      lastSyncedAt: at,
      lastError: errors.length ? errors.join('; ') : null,
    });
  }

  private async refreshCounts(): Promise<void> {
    const db = getDb();
    this.patch({
      pending: await pendingCount(),
      dead: await deadCount(),
      photosPending: await db.photos.count(),
    });
  }

  private patch(next: Partial<SyncStatus>): void {
    this.status = { ...this.status, ...next };
    for (const listener of this.listeners) listener(this.status);
  }
}

let engine: SyncEngine | null = null;

/** The app wide engine. Created once in main.tsx, read everywhere else. */
export function createSyncEngine(client: DiaClient, options?: SyncEngineOptions): SyncEngine {
  engine = new SyncEngine(client, options);
  return engine;
}

export function getSyncEngine(): SyncEngine {
  if (!engine) throw new Error('createSyncEngine has not run yet. Call it in main.tsx.');
  return engine;
}

/** Tests only. */
export function clearSyncEngine(): void {
  engine?.stop();
  engine = null;
}
